import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  applySubscriptionFallbackIfDue,
  refreshTokenBalance,
  getTokenBalance,
  consumeToken,
  consumeTokens,
  getHoursForDate,
  isShopClosedOn,
  isWithinOpeningHours,
} from '../../services/shopState';
import { createDeal, getRetailerDealCoords } from '../../services/apiService';
import './ShopNewDealPage.css';
import { DEAL_CATEGORIES, normalizeCategory } from '../../constants/categories';

const isDemoMode = () => sessionStorage.getItem('walkin_demo_mode') === 'true';

function buildDiscountLabel(type, value) {
  if (type === 'percent') return `${value || 0}% korting`;
  if (type === 'amount') return `€${value || 0} korting`;
  return value ? `Gratis: ${value}` : 'Gratis item';
}

const formatDutchDate = (d) =>
  d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });

const formatDutchDay = (d) =>
  d.toLocaleDateString('nl-NL', { weekday: 'long' });

const isoDate = (d) => d.toISOString().split('T')[0];

const dayDiff = (startStr, endStr) => {
  const a = new Date(startStr + 'T00:00:00');
  const b = new Date(endStr   + 'T00:00:00');
  return Math.round((b - a) / 86400000) + 1;
};

// Single platform-wide list — the walker feed filters on exactly these.
const CATEGORIES = DEAL_CATEGORIES;

// The picker is gone, but the field still travels with every deal — the walker
// side and the stored records both read it.
const DEFAULT_EMOJI = '🏷️';
const DISCOUNT_TYPES = [
  { key: 'percent', label: 'Percentage' },
  { key: 'amount',  label: 'Vast bedrag' },
  { key: 'free',    label: 'Gratis item' },
];

function readShopProfile() {
  try { return JSON.parse(sessionStorage.getItem('walkin_shop_profile') || '{}'); }
  catch { return {}; }
}

// Always look up the shop's current area/name from the canonical localStorage
// record (admin edits go there). Session is treated as a stale cache.
function readCurrentShop() {
  const email = sessionStorage.getItem('walkin_shop_email');
  if (!email) return null;
  let registered = [];
  let adminCreated = [];
  try { registered = JSON.parse(localStorage.getItem('walkin_shops') || '[]'); } catch { registered = []; }
  try { adminCreated = JSON.parse(localStorage.getItem('walkin_admin_shops') || '[]'); } catch { adminCreated = []; }
  const all = [...registered, ...adminCreated];
  return all.find((s) => (s.email || '').toLowerCase() === email.toLowerCase()) || null;
}

export default function ShopNewDealPage() {
  const navigate = useNavigate();
  const today = new Date();
  const todayIso = isoDate(today);
  const profile = readShopProfile();
  const todayHours = getHoursForDate(profile, today);
  const defaultStartTime =
    todayHours && !todayHours.closed ? (todayHours.open || '09:00') : '09:00';

  const [balance, setBalance] = useState(getTokenBalance());
  // Pull the current wallet from the backend so a balance an admin just
  // changed is what this page shows and checks against.
  useEffect(() => {
    let cancelled = false;
    refreshTokenBalance().then(() => { if (!cancelled) setBalance(getTokenBalance()); });
    return () => { cancelled = true; };
  }, []);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  // A shop that picked its branche in the profile does not get to file a deal
  // under a different one; without a profile category the field stays open so
  // deal creation is never blocked on it.
  const profileCategory = profile?.category ? normalizeCategory(profile.category) : '';
  const [category, setCategory] = useState(profileCategory || CATEGORIES[0]);
  // New scheduling fields
  const [startDate, setStartDate] = useState(todayIso);
  const [startTime, setStartTime] = useState(defaultStartTime);
  const [endDate, setEndDate]     = useState(todayIso);
  const [discountType, setDiscountType] = useState('percent');
  const [discountValue, setDiscountValue] = useState('');
  const [success, setSuccess] = useState(false);
  const [toast, setToast] = useState('');

  const currentShop = readCurrentShop();
  // Reflect any admin-side edits immediately by syncing the session.
  if (currentShop) {
    if (currentShop.area) sessionStorage.setItem('walkin_shop_area', currentShop.area);
    if (currentShop.shopName) sessionStorage.setItem('walkin_shop_name', currentShop.shopName);
    if (currentShop.subscription) {
      sessionStorage.setItem('walkin_shop_subscription', currentShop.subscription);
    }
    // Tokens are not synced from the browser record: the backend wallet is
    // the source of truth (see refreshTokenBalance below).
  }
  const area =
    currentShop?.area ||
    sessionStorage.getItem('walkin_shop_area') ||
    'Winkelcentrum Gelderlandplein';

  // Derived schedule info
  const startDateObj = useMemo(() => new Date(startDate + 'T00:00:00'), [startDate]);
  const endDateObj   = useMemo(() => new Date(endDate   + 'T00:00:00'), [endDate]);

  const closedOnStart = isShopClosedOn(profile, startDateObj);
  const closedOnEnd   = isShopClosedOn(profile, endDateObj);
  const startHours    = getHoursForDate(profile, startDateObj);
  const startWithinHours = startHours && !startHours.closed
    ? isWithinOpeningHours(profile, startDateObj, startTime)
    : true;

  const durationDays = dayDiff(startDate, endDate);

  // Build the scheduled validFrom datetime (used to decide gepland vs actief)
  const scheduledValidFrom = useMemo(
    () => new Date(`${startDate}T${startTime}:00`),
    [startDate, startTime]
  );
  const isScheduled = scheduledValidFrom.getTime() > Date.now();

  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(''), 2400);
    return () => clearTimeout(id);
  }, [toast]);

  const [submitting, setSubmitting] = useState(false);

  // Shared time/date derivations for both the demo and live paths.
  const buildTimes = () => {
    const validFromIso = scheduledValidFrom.toISOString();
    const expiresAtDate = new Date(`${endDate}T00:00:00`);
    expiresAtDate.setHours(23, 59, 59, 0);
    return { validFromIso, expiresAtIso: expiresAtDate.toISOString() };
  };

  // Demo mode keeps the original localStorage-based deal creation so the
  // offline/local submit path still works.
  const submitDemo = () => {
    const consumed = consumeToken();
    if (!consumed) { setBalance(getTokenBalance()); return; }
    setBalance(consumed);

    const { validFromIso, expiresAtIso } = buildTimes();
    const freshShop = readCurrentShop();
    const dealArea = freshShop?.area || sessionStorage.getItem('walkin_shop_area');
    const ownerName =
      freshShop?.shopName || sessionStorage.getItem('walkin_shop_name') || 'Mijn winkel';
    const ownerEmail = sessionStorage.getItem('walkin_shop_email') || '';

    const newDeal = {
      id: Date.now().toString(),
      title: (title || '').trim() || 'Naamloze deal',
      description, emoji: DEFAULT_EMOJI, category,
      shopName: ownerName, createdBy: ownerName, ownerEmail, area: dealArea,
      status: isScheduled ? 'gepland' : 'actief',
      validFrom: validFromIso, startDate, startTime, endDate,
      validFromTime: startTime, validUntilTime: '23:59',
      scheduledFor: isScheduled ? validFromIso : null,
      discountType, discountValue, expiresAt: expiresAtIso,
      createdAt: new Date().toISOString(),
      views: 0, redemptions: 0, tokensUsed: 1,
      code: Math.floor(100000 + Math.random() * 900000).toString(),
    };

    const existing = JSON.parse(localStorage.getItem('walkin_shop_deals') || '[]');
    existing.push(newDeal);
    localStorage.setItem('walkin_shop_deals', JSON.stringify(existing));
    window.dispatchEvent(new Event('walkin-deals-change'));

    try {
      const codes = JSON.parse(sessionStorage.getItem('walkin_qr_codes') || '[]');
      if (!codes.includes(newDeal.code)) {
        codes.push(newDeal.code);
        sessionStorage.setItem('walkin_qr_codes', JSON.stringify(codes));
      }
    } catch { /* noop */ }

    setToast(consumed.unlimited
      ? 'Deal aangemaakt — Unlimited abonnement actief.'
      : `1 token gebruikt. Nog ${consumed.available} beschikbaar.`);
    setSuccess(true);
    setTimeout(() => navigate('/shop/dashboard'), 1800);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (closedOnStart || submitting) return; // hard-blocked

    if (isDemoMode()) { submitDemo(); return; }

    // A cancelled Unlimited plan that has run past its end date reverts to the
    // token model here, before anything is spent, so this deal is charged
    // under the plan that actually applies today.
    applySubscriptionFallbackIfDue();

    // Live mode: don't spend the token until the backend accepts the deal.
    const bal = getTokenBalance();
    if (!bal.unlimited && bal.available < durationDays) { setBalance(bal); return; }

    setSubmitting(true);
    setToast('');
    try {
      const { validFromIso, expiresAtIso } = buildTimes();
      // The backend requires numeric coordinates on the deal. getRetailerDealCoords
      // falls back from the retailer record to the location saved in the shop
      // profile to the retailer's area centroid, so this only fails when the
      // retailer has no area either.
      const coords = await getRetailerDealCoords();
      if (!coords) {
        throw new Error(
          'Winkellocatie ontbreekt — stel je winkellocatie in via Profiel en probeer opnieuw.'
        );
      }

      await createDeal({
        title: (title || '').trim() || 'Naamloze deal',
        description,
        category,
        emoji: DEFAULT_EMOJI,
        discountType,
        discountValue,
        discountLabel: buildDiscountLabel(discountType, discountValue),
        validFrom: validFromIso,
        expiresAt: expiresAtIso,
        validFromTime: `${startTime || '09:00'}:00`,
        validUntilTime: '23:59:00',
        status: isScheduled ? 'gepland' : 'actief',
        lat: coords.lat,
        lng: coords.lng,
        // Stamp the retailer's gebied on the deal. POST /deals stores areaId
        // but it was never sent, so every deal was created with areaId: null.
        areaId: coords.areaId || undefined,
      });

      const consumed = await consumeTokens(durationDays);
      if (consumed) setBalance(consumed);
      setToast(consumed?.unlimited
        ? 'Deal aangemaakt — Unlimited abonnement actief.'
        : `${durationDays} token${durationDays !== 1 ? 's' : ''} gebruikt. Nog ${consumed?.available ?? 0} beschikbaar.`);
      setSuccess(true);
      setTimeout(() => navigate('/shop/dashboard'), 1800);
    } catch (err) {
      setToast(err?.message || 'Deal aanmaken mislukt. Probeer opnieuw.');
    } finally {
      setSubmitting(false);
    }
  };

  // Feature 5: blocking screen when 0 tokens (unlimited never blocks)
  if (!balance.unlimited && balance.available <= 0) {
    return (
      <div className="shop-new shop-new--blocked">
        <header className="shop-new__header">
          <button className="shop-new__back" onClick={() => navigate(-1)} aria-label="Terug">←</button>
          <h1 className="shop-new__title">Nieuwe deal</h1>
          <span className="shop-new__spacer" />
        </header>

        <main className="shop-new__blocked">
          <div className="shop-new__lock">🔒</div>
          <h2 className="shop-new__blocked-title">Geen deal tokens beschikbaar</h2>
          <p className="shop-new__blocked-sub">
            Je hebt geen tokens meer om een nieuwe deal aan te maken.
          </p>

          <div className="shop-new__balance">
            <div className="shop-new__balance-row">
              <span>Gebruikt</span>
              <strong>{balance.used}</strong>
            </div>
            <div className="shop-new__balance-row">
              <span>Beschikbaar</span>
              <strong>{balance.available}</strong>
            </div>
          </div>

          <button
            className="shop-new__buy"
            onClick={() => navigate('/shop/tokens')}
          >
            Tokens kopen →
          </button>
          <button
            className="shop-new__deals-link"
            onClick={() => navigate('/shop/deals')}
          >
            Bekijk mijn deals →
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="shop-new">
      <header className="shop-new__header">
        <button
          className="shop-new__back"
          onClick={() => navigate(-1)}
          aria-label="Terug"
        >
          ←
        </button>
        <h1 className="shop-new__title">Nieuwe deal</h1>
        <span className="shop-new__spacer" />
      </header>

      <div className="shop-new__area">📍 Deal voor: <strong>{area}</strong></div>
      <div className="shop-new__token-pill">
        {balance.unlimited
          ? '✅ Unlimited abonnement — onbeperkt deals'
          : `🪙 ${balance.available} ${balance.available === 1 ? 'token' : 'tokens'} beschikbaar`}
      </div>

      <form className="shop-new__form" onSubmit={handleSubmit}>
        <label className="shop-new__field">
          <span className="shop-new__label">Deal titel</span>
          <input
            className="shop-new__input"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="bijv. 2 voor de prijs van 1"
            required
          />
        </label>

        <label className="shop-new__field">
          <span className="shop-new__label">Beschrijving</span>
          <textarea
            className="shop-new__textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Beschrijf de deal voor klanten..."
            rows={3}
          />
        </label>

        <label className="shop-new__field">
          <span className="shop-new__label">Categorie</span>
          <select
            className="shop-new__select"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            disabled={!!profileCategory}
          >
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {profileCategory && (
            <small className="shop-new__hint">Overgenomen uit je winkelprofiel</small>
          )}
        </label>

        <fieldset className="shop-new__schedule">
          <legend>Geldigheid</legend>

          <div className="shop-new__row">
            <label className="shop-new__field shop-new__field--half">
              <span className="shop-new__label">Startdatum</span>
              <input
                className="shop-new__input"
                type="date"
                value={startDate}
                min={todayIso}
                onChange={(e) => {
                  const v = e.target.value;
                  setStartDate(v);
                  // Bump endDate forward if it would now be earlier than start
                  if (v > endDate) setEndDate(v);
                }}
              />
              <span className="shop-new__hint">{formatDutchDate(startDateObj)}</span>
            </label>
            <label className="shop-new__field shop-new__field--half">
              <span className="shop-new__label">Starttijd</span>
              <input
                className="shop-new__input"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
              <span className="shop-new__hint">Deal wordt actief om deze tijd</span>
            </label>
          </div>

          <label className="shop-new__field">
            <span className="shop-new__label">Einddatum</span>
            <input
              className="shop-new__input"
              type="date"
              value={endDate}
              min={startDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
            <span className="shop-new__hint">
              ⏰ Deal verloopt om 23:59 op einddatum — deals zijn altijd geldig
              tot het einde van de dag
            </span>
          </label>

          <div className="shop-new__duration">
            {durationDays === 1
              ? `📅 Eén dag — ${formatDutchDate(startDateObj)}`
              : `📅 Meerdaagse deal: ${durationDays} dagen — ${formatDutchDate(startDateObj)} t/m ${formatDutchDate(endDateObj)}`}
          </div>

          {closedOnStart && (
            <div className="shop-new__warn shop-new__warn--red">
              ⚠️ Je winkel is gesloten op {formatDutchDay(startDateObj)}!
              Deals worden niet getoond als je winkel gesloten is. Kies een
              andere datum.
            </div>
          )}

          {!closedOnStart && closedOnEnd && (
            <div className="shop-new__warn shop-new__warn--orange">
              ⚠️ Let op: je winkel is gesloten op de einddatum
              ({formatDutchDay(endDateObj)}). De deal verloopt sowieso om 23:59.
            </div>
          )}

          {!closedOnStart && startHours && !startWithinHours && (
            <div className="shop-new__warn shop-new__warn--orange">
              ⚠️ Starttijd {startTime} valt buiten je openingstijden
              ({startHours.open}–{startHours.close}). Klanten kunnen binnenlopen
              terwijl je gesloten bent.
            </div>
          )}

          {isScheduled && !closedOnStart && (
            <div className="shop-new__sched">
              📅 Deal wordt automatisch actief op{' '}
              {formatDutchDate(startDateObj)} om {startTime}.
            </div>
          )}
        </fieldset>

        <div className="shop-new__field">
          <span className="shop-new__label">Korting type</span>
          <div className="shop-new__radios">
            {DISCOUNT_TYPES.map((t) => (
              <label
                key={t.key}
                className={`shop-new__radio${discountType === t.key ? ' shop-new__radio--selected' : ''}`}
              >
                <input
                  type="radio"
                  name="discountType"
                  value={t.key}
                  checked={discountType === t.key}
                  onChange={() => setDiscountType(t.key)}
                />
                <span>{t.label}</span>
              </label>
            ))}
          </div>
        </div>

        <label className="shop-new__field">
          <span className="shop-new__label">
            Korting waarde {discountType === 'percent' ? '(%)' : discountType === 'amount' ? '(€)' : '(item)'}
          </span>
          <input
            className="shop-new__input"
            type={discountType === 'free' ? 'text' : 'number'}
            value={discountValue}
            onChange={(e) => setDiscountValue(e.target.value)}
            placeholder={discountType === 'percent' ? 'bijv. 20' : discountType === 'amount' ? 'bijv. 5' : 'bijv. Stroopwafel'}
          />
        </label>

        <button
          type="submit"
          className="shop-new__submit"
          disabled={closedOnStart || submitting}
        >
          {submitting ? 'Bezig…' : balance.unlimited
            ? (isScheduled ? 'Deal inplannen' : 'Deal activeren')
            : (isScheduled
                ? `Deal inplannen — kost ${durationDays} token${durationDays !== 1 ? 's' : ''}`
                : `Deal activeren — kost ${durationDays} token${durationDays !== 1 ? 's' : ''}`)}
        </button>
      </form>

      {toast && <div className="shop-new__toast">{toast}</div>}

      {success && (
        <div className="shop-new__overlay" role="dialog" aria-live="polite">
          <div className="shop-new__success">
            <div className="shop-new__success-circle">✅</div>
            <h2 className="shop-new__success-title">Deal is live!</h2>
            <p className="shop-new__success-text">
              Klanten in {area} zien jouw deal nu.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
